import { useState, useEffect, useMemo, useCallback } from 'react';
import './locating-page.css';
import NavigationV2 from '../../../../components/v2/NavigationV2.jsx';
import {
    getCampusId, getCampusList, updateSessionCampus,
} from '../../../../services/session.js';
import { fetchLocatingWidgetUrl } from './api/locatingApi.js';

/**
 * LocatingPage — embeds the Inovonics "Find Roaming Device" widget.
 *
 * Only routable when campusData.offsite === true && campusData.offsite_id === 14
 * (gated by Community.jsx). Fetches a fresh widget URL on mount and whenever
 * the campus changes, then renders it inside an iframe per the Inovonics
 * Cloud Integration Guide §9.5.
 */
const LocatingPage = () => {
    const sessionCampusId = getCampusId();
    const campusList = useMemo(() => getCampusList() || [], []);

    const [campusId, setCampusId] = useState(sessionCampusId || 0);
    const [widgetUrl, setWidgetUrl] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const campusName = useMemo(() => {
        const match = campusList.find((c) => (c.campus_id || c.CampusId) === campusId);
        return match?.campus_name || match?.CampusName || '';
    }, [campusList, campusId]);

    const loadWidget = useCallback(async () => {
        if (!campusId) return;
        setLoading(true);
        setError('');
        setWidgetUrl('');
        try {
            const url = await fetchLocatingWidgetUrl(campusId);
            if (url) {
                setWidgetUrl(url);
            } else {
                setError('The real-time locating widget is currently unavailable for this campus. Please contact your administrator.');
            }
        } catch (err) {
            console.error('[LocatingPage] Failed to load widget URL:', err);
            setError('Failed to load the real-time locating widget. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [campusId]);

    useEffect(() => { loadWidget(); }, [loadWidget]);

    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ps-page">
                    {/* PAGE HEADER */}
                    <div className="ps-page-header">
                        <div className="ps-page-header__inner">
                            <div className="ps-breadcrumb-row">
                                <div className="ps-breadcrumb">
                                    <span className="ps-breadcrumb__text">Community</span>
                                    <svg className="rp-icon-md ps-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ps-breadcrumb__current">Real-Time Locating</span>
                                </div>

                                <div className="ps-campus-row">
                                    <label htmlFor="locating-campus-selector" className="ps-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="locating-campus-selector"
                                            value={campusId}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value, 10);
                                                setCampusId(val);
                                                updateSessionCampus(val);
                                            }}
                                            className="ps-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id || c.CampusId} value={c.campus_id || c.CampusId}>
                                                    {c.campus_name || c.CampusName}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusName}</span>
                                    )}
                                </div>
                            </div>

                            <h1 className="ps-page-title">Real-Time Locating</h1>
                        </div>
                    </div>

                    {/* CONTENT */}
                    <div className="lp-iframe-wrap">
                        {loading ? (
                            <div className="lp-status">
                                <div className="lp-spinner" aria-hidden="true" />
                                <p className="lp-status__title">Loading locating widget…</p>
                            </div>
                        ) : error ? (
                            <div className="lp-status">
                                <p className="lp-status__title">Unable to load locating widget</p>
                                <p className="lp-status__message">{error}</p>
                                <button type="button" onClick={loadWidget} className="lp-retry-btn">Retry</button>
                            </div>
                        ) : widgetUrl ? (
                            <iframe
                                key={widgetUrl}
                                title="Real-Time Locating"
                                src={widgetUrl}
                                className="lp-iframe"
                                sandbox="allow-same-origin allow-scripts"
                                frameBorder="0"
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

export default LocatingPage;
